
import _ from "lodash";
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { ManualEntryFormStateType } from "../../components/manual-entry-form/ManualEntryFormUtils";


function getInitialDate() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
}

type ManualEntryFormViewState = {
    selectedDate: Date,
    endDate: Date;
    viewOptionState: ViewOptionStateEnum;
    formState: ManualEntryFormStateType;
};


const initialState: ManualEntryFormViewState = {
    selectedDate: getInitialDate(),
    endDate: getInitialDate(),
    viewOptionState: ViewOptionStateEnum.Default,
    formState: {
        isDirty: false,
        isValid: true
    }
};

enum ManualEntryFormActionType {
    ResetOptionsRequested = 'RESET_OPTIONS_REQUESTED',
    SelectedDateChanged = "SELECTED_DATE_CHANGED",
    FormStateChanged = "FORM_STATE_CHANGED",
    EndDateChanged = "END_DATE_CHANGED",
}

type ManualEntryFormActions = | {
    type: ManualEntryFormActionType.ResetOptionsRequested;
} | {
    type: ManualEntryFormActionType.SelectedDateChanged;
    payload: Date;
} | {
    type: ManualEntryFormActionType.FormStateChanged;
    payload: ManualEntryFormStateType;
} | {
    type: ManualEntryFormActionType.EndDateChanged;
    payload: Date;
};

function isDirty(value: ManualEntryFormViewState) {
    const endDate = new Date(value.endDate);
    endDate.setHours(0, 0, 0, 0);
    return  value.selectedDate.valueOf() !== initialState.selectedDate.valueOf() ||
        endDate.valueOf() !== initialState.endDate.valueOf();
}

function manualEntryFormReducer(state: ManualEntryFormViewState, action: ManualEntryFormActions): ManualEntryFormViewState {

    let newState:ManualEntryFormViewState = _.cloneDeep(state);

    switch (action.type) {
    
        case ManualEntryFormActionType.ResetOptionsRequested:
            return {...initialState}

        case ManualEntryFormActionType.SelectedDateChanged:
            newState.selectedDate = action.payload;
            newState.viewOptionState = isDirty(newState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return newState;

        case ManualEntryFormActionType.EndDateChanged:
            newState.endDate = action.payload;
            newState.viewOptionState = isDirty(newState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return newState;

        case ManualEntryFormActionType.FormStateChanged:
            newState.formState = action.payload;
            return newState;

        default:
            return state;
    }
}

export type ManualEntryFormState = {
    state: ManualEntryFormViewState;
    resetOptions: () => void;
    setSelectedDate: (value: Date) => void;
    setEndDate: (value: Date) => void;
    setFormState: (value: ManualEntryFormStateType) => void;
}

export default function useManualEntryFormState() {

    // by using a custom hook we could include  other state to work on such as app settings state
    // also this simplifies use of dispatch by providing state methods, this would alleviate the
    // need to use an enum for the action types
    const [state, dispatch] = useReducer(manualEntryFormReducer, {...initialState});

    const resetOptions = useCallback(() => dispatch({
        type: ManualEntryFormActionType.ResetOptionsRequested,
    }), []);

    const setSelectedDate = useCallback((value: Date) => dispatch({
        type: ManualEntryFormActionType.SelectedDateChanged,
        payload: value
    }), []);

    const setEndDate = useCallback((value: Date) => dispatch({
        type: ManualEntryFormActionType.EndDateChanged,
        payload: value
    }), []);

    const setFormState = useCallback((value: ManualEntryFormStateType) => dispatch({
        type: ManualEntryFormActionType.FormStateChanged,
        payload: value
    }), []);


    return {
        state,
        resetOptions,
        setSelectedDate,
        setEndDate,
        setFormState
    };
}
